import isHotkey from "is-hotkey";
import React, { KeyboardEvent, PointerEvent, useCallback, useMemo } from "react";
import { Editor, Element as SlateElement, Transforms, createEditor } from "slate";
import { withHistory } from "slate-history";
import { Editable, RenderElementProps, RenderLeafProps, Slate, useSlate, withReact } from "slate-react";
import { Box } from "@mui/material";
import { Button, Toolbar } from "./rich-text-components/index";
import { CustomEditor, CustomElementType, CustomTextKey } from "./richTextTypes";
import BoldIcon from "../../../assets/svg-fc/BoldIcon";
import ItalicIcon from "../../../assets/svg-fc/ItalicIcon";
import UnderlineIcon from "../../../assets/svg-fc/UnderlineIcon";
import StrikeThroughIcon from "../../../assets/svg-fc/StrikeThroughIcon";
import BulletListIcon from "../../../assets/svg-fc/BulletListIcon";
import NumberedListIcon from "../../../assets/svg-fc/NumberedListIcon";
import "./richTextEditor.scss";

const HOTKEYS: Record<string, CustomTextKey> = {
	"mod+b": "bold",
	"mod+i": "italic",
	"mod+u": "underline",
	"mod+s": "strikethrough",
};

const LIST_TYPES = ["numbered-list", "bulleted-list"] as const;

type ListType = (typeof LIST_TYPES)[number];
type CustomElementFormat = CustomElementType | ListType;

interface RichTextEditorProps {
	value: string;
	onChange: (value: string) => void;
	placeholder?: string;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
	value,
	onChange,
	placeholder = "Enter text here",
}) => {
	const renderElement = useCallback((props: RenderElementProps) => <Element {...props} />, []);
	const renderLeaf = useCallback((props: RenderLeafProps) => <Leaf {...props} />, []);
	const editor = useMemo(() => withHistory(withReact(createEditor())), []);
	const initialValue = value ? JSON.parse(value) : [{ type: "paragraph", children: [{ text: "" }] }];

	return (
		<Box className="richTextEditorContainer">
			<Slate
				editor={editor}
				initialValue={initialValue}
				onChange={(value) => {
					const isAstChange = editor.operations.some((op) => "set_selection" !== op.type);
					if (isAstChange) {
						const content = JSON.stringify(value);
						onChange(content);
					}
				}}
			>
				<Editable
					renderElement={renderElement}
					renderLeaf={renderLeaf}
					placeholder={placeholder}
					spellCheck
					onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
						for (const hotkey in HOTKEYS) {
							if (isHotkey(hotkey, event as any)) {
								event.preventDefault();
								const mark = HOTKEYS[hotkey];
								toggleMark(editor, mark);
							}
						}
					}}
				/>
				<Toolbar className="richTextToolbar">
					<MarkButton format="bold" icon={<BoldIcon />} />
					<MarkButton format="italic" icon={<ItalicIcon />} />
					<MarkButton format="underline" icon={<UnderlineIcon />} />
					<MarkButton format="strikethrough" icon={<StrikeThroughIcon />} />
					<BlockButton format="bulleted-list" icon={<BulletListIcon />} />
					<BlockButton format="numbered-list" icon={<NumberedListIcon />} />
				</Toolbar>
			</Slate>
		</Box>
	);
};

const toggleBlock = (editor: CustomEditor, format: CustomElementFormat) => {
	const isActive = isBlockActive(editor, format);
	const isList = isListType(format);

	Transforms.unwrapNodes(editor, {
		match: (n) => !Editor.isEditor(n) && SlateElement.isElement(n) && isListType(n.type),
		split: true,
	});
	let newProperties: Partial<SlateElement>;
	newProperties = {
		type: isActive ? "paragraph" : isList ? "list-item" : format,
	};
	Transforms.setNodes<SlateElement>(editor, newProperties);

	if (!isActive && isList) {
		const block = { type: format, children: [] };
		Transforms.wrapNodes(editor, block);
	}
};

const toggleMark = (editor: CustomEditor, format: CustomTextKey) => {
	const isActive = isMarkActive(editor, format);

	if (isActive) {
		Editor.removeMark(editor, format);
	} else {
		Editor.addMark(editor, format, true);
	}
};

const isBlockActive = (editor: CustomEditor, format: CustomElementFormat) => {
	const { selection } = editor;
	if (!selection) return false;

	const [match] = Array.from(
		Editor.nodes(editor, {
			at: Editor.unhangRange(editor, selection),
			match: (n) => {
				if (!Editor.isEditor(n) && SlateElement.isElement(n)) {
					return n.type === format;
				}
				return false;
			},
		}),
	);

	return !!match;
};

const isMarkActive = (editor: CustomEditor, format: CustomTextKey) => {
	const marks = Editor.marks(editor);
	return marks ? marks[format] === true : false;
};

const Element = ({ attributes, children, element }: RenderElementProps) => {
	const style: React.CSSProperties = {};
	switch (element.type) {
		case "bulleted-list":
			return (
				<ul style={style} {...attributes}>
					{children}
				</ul>
			);
		case "list-item":
			return (
				<li style={style} {...attributes}>
					{children}
				</li>
			);
		case "numbered-list":
			return (
				<ol style={style} {...attributes}>
					{children}
				</ol>
			);
		default:
			return (
				<p style={style} {...attributes}>
					{children}
				</p>
			);
	}
};

const Leaf = ({ attributes, children, leaf }: RenderLeafProps) => {
	if (leaf.bold) {
		children = <strong>{children}</strong>;
	}

	if (leaf.italic) {
		children = <em>{children}</em>;
	}

	if (leaf.underline) {
		children = <u>{children}</u>;
	}

	if (leaf.strikethrough) {
		children = <s>{children}</s>;
	}

	return <span {...attributes}>{children}</span>;
};

interface BlockButtonProps {
	format: CustomElementFormat;
	icon: React.ReactNode;
}

const BlockButton = ({ format, icon }: BlockButtonProps) => {
	const editor = useSlate();
	return (
		<Button
			active={isBlockActive(editor, format)}
			onPointerDown={(event: PointerEvent<HTMLButtonElement>) => event.preventDefault()}
			onClick={() => toggleBlock(editor, format)}
		>
			{icon}
		</Button>
	);
};

interface MarkButtonProps {
	format: CustomTextKey;
	icon: React.ReactNode;
}

const MarkButton = ({ format, icon }: MarkButtonProps) => {
	const editor = useSlate();
	return (
		<Button
			active={isMarkActive(editor, format)}
			onPointerDown={(event: PointerEvent<HTMLButtonElement>) => event.preventDefault()}
			onClick={() => toggleMark(editor, format)}
		>
			{icon}
		</Button>
	);
};

const isListType = (format: CustomElementFormat): format is ListType => {
	return LIST_TYPES.includes(format as ListType);
};

export default RichTextEditor;
